import type { VodDto, WbsClient } from '@bldsoft/cappie/api/wbs'
import type {
  API,
  CategoryWithVodsApi,
  MediaUrl,
  Page,
  Vod,
  VodCategory,
} from '@setplex/tria-api'
import type { AdapterDefaults } from '../../index.h'

import { ensureOk, payload } from '@bldsoft/cappie/rest'
import {
  RECOMMENDED_AMOUNT_VODS,
  RECOMMENDED_AMOUNT_VODS_AND_CURRENT,
} from '@setplex/tria-api'
import { sample } from 'effector'
import { DEFAULT_PAGE_SIZE } from '../../constants/generic'
import { getFirstPagePaginateInfo } from '../../utils/pageInfo'
import { formatVod, formatVodUpdatedOnly } from './vods.format'

const featuredCategoryMock = {
  id: 0,
  name: 'Featured',
  sortOrder: 0,
  vodQuantity: 100, // not used atm, but not clear what to leave there
  parentCategoryId: undefined,
  subCategories: [],
}

export function use(
  client: WbsClient,
  vods: API['vods'],
  _api: API,
  defaults: AdapterDefaults
): void {
  // GET /v3/vods
  vods.getPageFx.use(
    async ({
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      ids,
      categoryId,
      q,
      tags,
      'only-favorites': onlyFavorites,
    }) => {
      const vodsPage = await payload(
        client.get.api.v3.vods({
          query: {
            page,
            count,
            ids,
            categoryId,
            q,
            tags,
            'only-favorites': onlyFavorites,
          },
        })
      )

      return {
        ...vodsPage,
        content: vodsPage.content.map(formatVod),
      }
    }
  )

  // GET /v3/library/vods
  vods.getPurchasedVodsFx.use(
    async ({
      page = 0,
      count = defaults.count || 36,
      rented = true,
      purchased = true,
    }) => {
      const vodsPage = await payload(
        client.get.api.v3.library.vods({
          query: {
            page,
            count,
            rented,
            purchased,
          },
        })
      )

      return {
        ...vodsPage,
        content: vodsPage.content.map(formatVod),
      }
    }
  )

  // GET /v3/vods?categoryId=categoryId
  vods.getVodsByCategoryIdFx.use(
    async ({
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      categoryId,
      collectionId,
      'only-favorites': onlyFavorites,
    }) => {
      const vodsPage = await payload(
        client.get.api.v3.vods({
          query: {
            page,
            count,
            categoryId,
            collectionId,
            'only-favorites': onlyFavorites,
          },
        })
      )

      return {
        ...vodsPage,
        content: vodsPage.content.map(formatVod),
      }
    }
  )

  // GET /v3/vods?updatedTimeOnly=true
  vods.getUpdatedOnlyFx.use(async () => {
    const updated = (await payload(
      client.get.api.v3.vods({ query: { updatedTimeOnly: true } })
    )) as unknown as VodDto[]
    return updated.map(formatVodUpdatedOnly)
  })

  // GET /v3/vods/{vodId}
  vods.getVodBaseFx.use(async ({ vodId }) => {
    const vod = await payload(client.get.api.v3.vods(vodId)())
    return formatVod(vod)
  })

  // GET /v3/vods/{vodId}/url
  vods.getUrlFx.use(async ({ vodId }) => {
    const media = await payload(client.get.api.v3.vods(vodId).url())
    return media as MediaUrl
  })

  // GET /v3/vods/{vodId}/trailer/url
  vods.getTrailerUrlFx.use(async ({ vodId }) => {
    const media = await payload(client.get.api.v3.vods(vodId).trailer.url())
    return media as MediaUrl
  })

  // GET /v3/vods/categories
  vods.getCategoriesFx.use(
    async ({ page, count = defaults.count || DEFAULT_PAGE_SIZE }) => {
      const categories = await payload(
        client.get.api.v3.vods.categories({ query: { page, count } })
      )
      return categories
    }
  )

  const getDefaultAllCategory = (): VodCategory => ({
    id: 0,
    name: 'All Movies',
    sortOrder: 0,
    vodQuantity: 0,
    parentCategoryId: 0,
    subCategories: [],
  })

  // GET /v3/vods/categories/{categoryId}
  vods.getVodCategoryFx.use(async ({ categoryId }) => {
    // as wbs got default category 'All' - 0, but could not return it, this tweak done
    if (categoryId === 0) return getDefaultAllCategory()

    const category = await payload(
      client.get.api.v3.vods.categories(categoryId)()
    )

    return category
  })

  // GET /v3/vods/categories/{categoryId}/subcategories
  vods.getSubcategoriesFx.use(
    async ({
      categoryId,
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
    }) => {
      const categories = await payload(
        client.get.api.v3.vods
          .categories(categoryId)
          .subcategories({ query: { page, count } })
      )
      return categories
    }
  )

  // PATCH /v3/vods/{vodId}
  vods.updateVodFx.use(
    async ({
      id,
      watched,
      favorite,
    }: {
      id: number
      watched?: boolean
      favorite?: boolean
    }) => {
      const body: { watched?: boolean; favorite?: boolean; id?: number } = {}
      if (watched != null) body.watched = watched
      if (favorite != null) body.favorite = favorite
      body.id = id
      await ensureOk(client.patch.api.v3.vods(id, { body }))
    }
  )

  // PUT /v3/vods/{vodId}/continue-watching
  vods.updateContinueWatchingFx.use(
    async ({ vodId, stoppedTime, lengthMillis }) => {
      const body: { stoppedTime: number; lengthMillis?: number } = {
        stoppedTime: 0,
      }

      // FIXME: why is it here?
      if (stoppedTime == null)
        throw new Error('Empty stoppedTime arg in updateContinueWatchingFx')

      body.stoppedTime = Number(stoppedTime)
      if (lengthMillis) body.lengthMillis = Number(lengthMillis)

      await ensureOk(client.put.api.v3.vods(vodId).continueWatching({ body }))
    }
  )

  // DELETE /v3/vods/{vodId}/continue-watching
  vods.removeContinueWatchingFx.use(async ({ vodId }) => {
    await ensureOk(client.delete.api.v3.vods(vodId).continueWatching())
  })

  // GET /v3/vods/?sort-by=updatedTime&sort-order=desc
  vods.getLastAddedMoviesFx.use(
    async ({
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      categoryId = 0,
      collectionId,
      'only-favorites': onlyFavorites,
    }) => {
      // however name is 'LastAdded' - 'sort-by=updatedTime' is default
      const vodsPage = await payload(
        client.get.api.v3.vods({
          query: {
            page,
            count,
            'sort-by': 'updatedTime',
            'sort-order': 'desc',
            categoryId,
            collectionId,
            'only-favorites': onlyFavorites,
          },
        })
      )

      return {
        ...vodsPage,
        content: vodsPage.content.map(formatVod),
      }
    }
  )

  const formatToRecommendedVod = (categoryId: number) => (vod: Vod) => ({
    ...vod,
    categoryId,
  })

  vods.getRecommendedVodsFx.use(
    async ({
      categoryId,
      collectionId,
      'only-favorites': onlyFavorites,
      vodId,
      ...rest
    }) => {
      let category: VodCategory

      if (categoryId === 0) {
        // You could not get that Featured category from b.e.
        category = featuredCategoryMock
      } else {
        category = await vods.getVodCategoryFx({ categoryId })
      }

      const formatVod = formatToRecommendedVod(categoryId)

      // FP-199
      const orderRecommended = (vodsArr: readonly Vod[]) => {
        const foundIndex = vodsArr.findIndex((show) => show.id === vodId)
        const currentIndex = foundIndex === -1 ? 0 : foundIndex

        const firstPart = vodsArr.slice(
          currentIndex,
          RECOMMENDED_AMOUNT_VODS_AND_CURRENT
        )
        const secondPart = vodsArr.slice(0, currentIndex)
        const orderedRecommended =
          currentIndex >= 0 ? [...firstPart, ...secondPart] : vodsArr

        const filterNoCurrent = orderedRecommended
          .filter((vod) => vod.id !== vodId)
          .slice(0, RECOMMENDED_AMOUNT_VODS)

        return {
          ...category,
          content: filterNoCurrent.map(formatVod),
          description: 'string', // TODO temporary mock for show, refactor later
        }
      }

      if ('vodIndex' in rest && typeof rest.vodIndex === 'number') {
        const count = defaults.count || DEFAULT_PAGE_SIZE

        if (count <= RECOMMENDED_AMOUNT_VODS)
          throw new Error('Issue with amounts of recommended vods logic')

        const indexInPage = rest.vodIndex % count

        const vodPageumber = Math.floor(rest.vodIndex / count)

        const isRightOffsetEnough =
          count - indexInPage >= RECOMMENDED_AMOUNT_VODS

        const firstRecommendedPage = await vods.getVodsByCategoryIdFx({
          page: vodPageumber,
          categoryId,
          collectionId,
          'only-favorites': onlyFavorites,
        })
        let secondRecommendedPage: readonly Vod[] = []
        if (!isRightOffsetEnough && !firstRecommendedPage.last)
          secondRecommendedPage = (
            await vods.getVodsByCategoryIdFx({
              page: vodPageumber + 1,
              categoryId,
              collectionId,
              'only-favorites': onlyFavorites,
            })
          ).content

        const twoPagesOfVods = [
          ...firstRecommendedPage.content,
          ...secondRecommendedPage,
        ]
        const amount = twoPagesOfVods.length

        if (amount <= RECOMMENDED_AMOUNT_VODS_AND_CURRENT)
          return orderRecommended(twoPagesOfVods)
        if (indexInPage + RECOMMENDED_AMOUNT_VODS > amount)
          return orderRecommended(
            twoPagesOfVods.slice(
              amount - RECOMMENDED_AMOUNT_VODS_AND_CURRENT,
              amount
            )
          )
        if (indexInPage + RECOMMENDED_AMOUNT_VODS <= amount)
          return orderRecommended(
            twoPagesOfVods.slice(
              indexInPage,
              indexInPage + RECOMMENDED_AMOUNT_VODS + 1
            )
          )
      }

      // Fallback algo. where 'vodIndex' not provided
      const vodsArr = await vods.getVodsByCategoryIdFx({
        page: 0,
        categoryId,
        collectionId,
        'only-favorites': onlyFavorites,
        count: RECOMMENDED_AMOUNT_VODS_AND_CURRENT,
      })

      return orderRecommended(vodsArr.content)
    }
  )

  vods.initVodsCategoryFx.use(async ({ categoryId, count }) => {
    const category = await vods.getVodCategoryFx({ categoryId })

    const vodsArr = await vods.loadVodsFx({ categoryId, page: 0, count })

    return {
      ...category,
      content: [...vodsArr.content],
    }
  })

  sample({
    clock: vods.initVodsSubCategoryFx,
    target: vods.loadParentCategoryVodFx,
  })

  vods.loadSubCategoriesWithVodsFx.use(async ({ page, count, categoryId }) => {
    // TODO fix that effect as this one is used as side effect
    const pageOfCategories = await vods.loadSubCategoriesFx({
      page,
      count,
      categoryId,
    })
    const categoriesIdsArray = pageOfCategories.content.map(
      (category) => category.id
    )
    const promises = categoriesIdsArray.map((categoryId) =>
      vods
        .loadVodsOfSubcategoryFx({
          page: 0,
          categoryId: Number(categoryId),
        })
        .catch(() => ({ content: [] }))
    )
    await Promise.all(promises)
  })

  // GET /v3/vods?only-recommended=true
  vods.getRealRecommendedVodsFx.use(
    async ({ page, count = defaults.count || DEFAULT_PAGE_SIZE }) => {
      const vodsPage = await payload(
        client.get.api.v3.vods({
          query: { 'only-recommended': true, page, count },
        })
      )

      return {
        ...vodsPage,
        content: vodsPage.content.map(formatVod),
      }
    }
  )

  // GET /v3/vods?recommended-to={vodId}
  vods.getPlayerRecommendedVodsFx.use(async ({ vodId, count = 4 }) => {
    const vodsPage = await payload(
      client.get.api.v3.vods({
        query: { 'recommended-to': vodId, count, page: 0 },
      })
    )

    return {
      ...vodsPage,
      content: vodsPage.content.map(formatVod),
    }
  })

  vods.getCategoriesWithVodsFx.use(async ({ page, count }) => {
    const pageOfCategories = await vods.getCategoriesFx({ page, count })
    const categoriesIdsArray = pageOfCategories.content.map(
      (category) => category.id
    )

    const vodsPromises = categoriesIdsArray.map((categoryId) =>
      vods
        .getVodsByCategoryIdFx({
          page: 0,
          categoryId: Number(categoryId),
        })
        .then((data) => ({
          categoryId: Number(categoryId),
          data,
        }))
        .catch(() => ({
          categoryId: Number(categoryId),
          data: undefined,
        }))
    )

    const vodsPages = await Promise.all(vodsPromises)

    const formattedCategory = pageOfCategories.content.map(
      (category): CategoryWithVodsApi => {
        const vodsPageOfCategory = vodsPages.find(
          (vods) => vods.categoryId === category.id
        )?.data

        const isMoreThanOnePage =
          !vodsPageOfCategory?.last ||
          vodsPageOfCategory?.content.length > DEFAULT_PAGE_SIZE

        const isVodsExist = vodsPageOfCategory?.content.length
        const pageOfVods: Page<Vod> = isVodsExist
          ? {
              ...vodsPageOfCategory,
              content: vodsPageOfCategory.content,
            }
          : { content: [], ...getFirstPagePaginateInfo({ totalElements: 0 }) }

        return {
          ...category,
          content: pageOfVods,
          isMoreThanOnePage,
          isMoreThanOnePageSubCategories:
            Number(category.subCategories?.length) > DEFAULT_PAGE_SIZE,
          subCategories: category.subCategories?.slice(0, DEFAULT_PAGE_SIZE),
        }
      }
    )

    return {
      ...pageOfCategories,
      content: formattedCategory,
    }
  })
}
